import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { devisAPI } from '../../lib/api';
import { Loader2, Download, AlertCircle } from 'lucide-react';
import DevisPdfViewer from '../../components/devis/DevisPdfViewer';

/**
 * Page de consultation client du devis.
 *
 * Le devis est affiché page à page (visualiseur pdf.js, DevisPdfViewer) à partir
 * du MÊME PDF que le téléchargement (/devis/public/{token}/pdf) : le lien web et
 * le PDF sont strictement identiques par construction. Le visualiseur trace le
 * temps passé PAR PAGE (heatmap de l'onglet Suivi). L'ouverture du lien (et la
 * promotion vu/relu → synchro phase HubSpot) est enregistrée côté backend lors
 * de l'appel getPublic ci-dessous.
 */
export default function DevisPublicPage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ok | error
  const [errorMsg, setErrorMsg] = useState('');
  // Libellés « quel bloc démarre sur quelle page » (suivi PAR BLOC) : renvoyés
  // par le backend, figés avec le PDF. Vides tant que le PDF n'est pas figé → le
  // visualiseur retombe alors sur « Page N ».
  const [blockPages, setBlockPages] = useState([]);
  // Empreinte de version du PDF (busting de cache) : ajoutée à l'URL du
  // visualiseur pour permettre la mise en cache dur du PDF figé côté navigateur.
  const [pdfVersion, setPdfVersion] = useState(null);

  // Aperçu commercial (?preview=1) : on propage le flag au backend pour qu'il
  // ne compte PAS cette visite comme une lecture client (ni suivi, ni balise).
  const isPreview = new URLSearchParams(window.location.search).get('preview') === '1';

  // Rafraîchissement des libellés bloc→page une fois le PDF chargé. Sur un devis
  // MODIFIÉ après envoi, `pdf_block_pages` est vidé puis re-figé par la requête
  // /pdf elle-même : au 1er chargement getPublic renvoyait une table vide. On la
  // relit alors — en mode APERÇU pour NE PAS re-compter l'ouverture (déjà
  // enregistrée par le getPublic initial). Une seule fois, et seulement si vide.
  const refreshedRef = useRef(false);
  const handleViewerLoaded = useCallback(async () => {
    // Rien à faire si on a déjà les libellés (devis envoyé non modifié) ou en
    // aperçu commercial. Une seule tentative par session.
    if (refreshedRef.current || isPreview || blockPages.length) return;
    refreshedRef.current = true;
    try {
      const { data } = await devisAPI.getPublic(token, true);
      const bp = data?.block_pages;
      if (Array.isArray(bp) && bp.length) setBlockPages(bp);
    } catch (e) { /* le rafraîchissement des libellés ne doit jamais casser l'affichage */ }
  }, [token, isPreview, blockPages.length]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Valide le jeton (expiré / introuvable / pas encore envoyé) avant
        // d'afficher le PDF, pour un message clair plutôt qu'une page vide.
        // En aperçu, on passe le flag pour ne PAS compter la visite comme lecture.
        const { data } = await devisAPI.getPublic(token, isPreview);
        if (cancelled) return;
        setBlockPages(Array.isArray(data?.block_pages) ? data.block_pages : []);
        setPdfVersion(data?.pdf_version || null);
        setState('ok');
      } catch (e) {
        if (cancelled) return;
        Sentry.captureException(e);
        setErrorMsg(e?.response?.data?.detail || "Ce lien n'est plus valide ou le devis est introuvable.");
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#faf9f7' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: '#02a96d' }} />
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#faf9f7', color: '#48544D', padding: 24, textAlign: 'center' }}>
        <AlertCircle className="w-8 h-8" style={{ color: '#d1493f' }} />
        <p style={{ fontSize: 15, fontWeight: 600, color: '#16211d' }}>Devis indisponible</p>
        <p style={{ fontSize: 13, maxWidth: 420 }}>{errorMsg}</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#faf9f7' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 16px', borderBottom: '1px solid #e3e9e6', background: '#fff' }}>
        <a
          href={devisAPI.publicPdfUrl(token, { preview: isPreview })}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: '#fff', background: '#02a96d', borderRadius: 9, padding: '8px 14px', textDecoration: 'none' }}
        >
          <Download className="w-4 h-4" /> Télécharger le PDF
        </a>
      </div>
      {/* Visualiseur PDF page à page : trace le temps passé PAR PAGE (heatmap de
          l'onglet Suivi). En aperçu commercial (preview), aucun suivi. */}
      <div style={{ flex: 1 }}>
        <DevisPdfViewer
          pdfUrl={devisAPI.publicPdfUrl(token, { inline: true, preview: isPreview, v: pdfVersion })}
          trackUrl={isPreview ? null : devisAPI.publicTrackUrl(token)}
          blockLabels={blockPages}
          onLoaded={handleViewerLoaded}
        />
      </div>
    </div>
  );
}
